import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatRelative } from '@/lib/time';
import { Thumbnail } from './Thumbnail';

type AnyRecord = Record<string, unknown>;

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  loading?: boolean;
  data: AnyRecord | null | undefined;
  // Field names to skip entirely (eg. id, raw blobs)
  hideFields?: string[];
  // Field names that should render as logo thumbnails
  thumbnailFields?: string[];
  topActions?: React.ReactNode;
};

const DEFAULT_HIDE = new Set(['id', 'docId']);

function isPlainObject(v: unknown): v is AnyRecord {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isLikelyDate(key: string, value: unknown): boolean {
  if (typeof value !== 'string') return false;
  if (!/(At|Date|Time|_at)$/i.test(key)) return false;
  return /\d{4}-\d{2}-\d{2}/.test(value) || /^\d+$/.test(value);
}

function classify(key: string, value: unknown): 'date' | 'badge' | 'thumb' | 'scalar' | 'list' | 'object' {
  if (value === null || value === undefined) return 'scalar';
  if (isLikelyDate(key, value)) return 'date';
  if (key === 'status' || key.endsWith('Status')) return 'badge';
  if (Array.isArray(value)) return 'list';
  if (isPlainObject(value)) return 'object';
  return 'scalar';
}

function renderValue(key: string, value: unknown, isThumb: boolean): React.ReactNode {
  if (value === null || value === undefined) return <span className="text-subtle">—</span>;
  if (isThumb && typeof value === 'string') return <Thumbnail path={value} alt={key} className="h-12 w-12" />;
  const kind = classify(key, value);
  if (kind === 'date') {
    const iso = typeof value === 'string' ? value : String(value);
    return <span title={iso}>{formatRelative(iso)}</span>;
  }
  if (kind === 'badge') {
    return <Badge className="bg-bg text-text">{String(value)}</Badge>;
  }
  if (kind === 'list') {
    const arr = value as unknown[];
    if (arr.length === 0) return <span className="text-subtle">empty</span>;
    if (arr.every((v) => typeof v === 'string' || typeof v === 'number')) {
      return <span className="text-text">{arr.map(String).join(', ')}</span>;
    }
    return <pre className="overflow-auto rounded-sm bg-bg p-2 text-[10.5px]">{JSON.stringify(arr, null, 2)}</pre>;
  }
  if (kind === 'object') {
    return <pre className="overflow-auto rounded-sm bg-bg p-2 text-[10.5px]">{JSON.stringify(value, null, 2)}</pre>;
  }
  // scalar
  if (typeof value === 'boolean') return <Badge className={value ? 'bg-success/10 text-success' : 'bg-bg text-muted'}>{String(value)}</Badge>;
  return <span className="break-all">{String(value)}</span>;
}

export function DetailDrawer({
  open, onClose, title, subtitle, loading, data,
  hideFields = [], thumbnailFields = [], topActions,
}: Props) {
  const hide = new Set([...DEFAULT_HIDE, ...hideFields]);
  const thumbs = new Set(thumbnailFields);

  // Order keys: dates last, scalars first, objects after
  const entries = data
    ? Object.entries(data).filter(([k]) => !hide.has(k))
    : [];
  const orderedEntries = entries.sort(([ka, va], [kb, vb]) => {
    const score = (k: string, v: unknown) => {
      const kind = classify(k, v);
      if (thumbs.has(k)) return 0;
      if (kind === 'badge') return 1;
      if (kind === 'scalar') return 2;
      if (kind === 'date') return 3;
      if (kind === 'list') return 4;
      return 5;
    };
    return score(ka, va) - score(kb, vb) || ka.localeCompare(kb);
  });

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        {topActions ? (
          <div className="mb-3 border-b border-divider pb-3">{topActions}</div>
        ) : null}
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {subtitle && <p className="text-[11px] text-muted">{subtitle}</p>}
        </SheetHeader>
        {loading ? (
          <div className="mt-6 space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : data ? (
          <dl className="mt-5 space-y-2.5">
            {orderedEntries.map(([k, v]) => (
              <div key={k} className="grid grid-cols-[120px_1fr] gap-3 border-b border-divider pb-2 last:border-b-0">
                <dt className="text-[10.5px] uppercase tracking-[0.04em] text-muted pt-0.5">{k}</dt>
                <dd className="text-[12px] text-text">{renderValue(k, v, thumbs.has(k))}</dd>
              </div>
            ))}
            {orderedEntries.length === 0 && (
              <p className="text-[12px] text-muted">No fields to show.</p>
            )}
          </dl>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
